import { useState, FormEvent } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { submitWarrantyClaim } from '@/lib/api/forms';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function WarrantyClaimForm() {
  const { theme } = useTheme();
  const light = theme === 'light';
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);
  const [batteryType, setBatteryType] = useState('');

  const bg = light ? 'bg-white border border-gray-100' : 'bg-[#1C2B3A] border border-white/5';
  const textPrimary = light ? 'text-gray-900' : 'text-white';
  const textSecondary = light ? 'text-gray-500' : 'text-white/50';
  const textMuted = light ? 'text-gray-400' : 'text-white/30';
  const divider = light ? 'border-gray-100' : 'border-white/5';
  const inputCls = light
    ? 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[var(--accent)]'
    : 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[var(--accent)]';
  const fieldCls = `w-full border rounded-xl px-4 py-3 text-sm outline-none transition-colors ${inputCls}`;
  const labelCls = `block text-xs font-bold uppercase tracking-wide mb-1.5 ${textMuted}`;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const desc = (form.elements.namedItem('issue_description') as HTMLTextAreaElement).value;
    if (desc.length > 500) return;
    setStatus('loading');
    try {
      await submitWarrantyClaim({
        firstName: (form.elements.namedItem('first_name') as HTMLInputElement).value,
        lastName: (form.elements.namedItem('last_name') as HTMLInputElement).value,
        email: (form.elements.namedItem('email') as HTMLInputElement).value,
        phone: (form.elements.namedItem('phone') as HTMLInputElement).value,
        batteryType: (form.elements.namedItem('battery_type') as HTMLSelectElement).value,
        batteryModel: (form.elements.namedItem('battery_model') as HTMLInputElement).value,
        serialNumber: (form.elements.namedItem('serial_number') as HTMLInputElement).value,
        purchaseDate: (form.elements.namedItem('purchase_date') as HTMLInputElement).value,
        retailerName: (form.elements.namedItem('retailer_name') as HTMLInputElement).value,
        issueType: (form.elements.namedItem('issue_type') as HTMLSelectElement).value,
        issueDescription: desc,
        vesselType: (form.elements.namedItem('vessel_type') as HTMLSelectElement).value,
        sourceUrl: window.location.href,
      });
      setStatus('success');
      form.reset();
      setCharCount(0);
      setBatteryType('');
    } catch { setStatus('error'); }
  };

  if (status === 'success') {
    return (
      <div id="claim-form" className={`rounded-2xl p-10 text-center scroll-mt-28 ${bg}`}>
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 text-white text-2xl"
          style={{ background: 'var(--accent)' }}
        >
          <i className="ri-check-double-line"></i>
        </div>
        <h3 className={`text-xl font-black mb-2 ${textPrimary}`}>Claim Submitted!</h3>
        <p className={`text-sm mb-6 max-w-sm mx-auto ${textSecondary}`}>
          We&apos;ve received your warranty claim. Our team will review it and get back to you within 2 business days.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white cursor-pointer whitespace-nowrap"
          style={{ background: 'var(--accent)' }}
        >
          <i className="ri-add-line"></i>
          Submit Another Claim
        </button>
      </div>
    );
  }

  return (
    <div id="claim-form" className={`rounded-2xl scroll-mt-28 overflow-hidden ${bg}`}>
      {/* Form header */}
      <div className="relative px-8 pt-8 pb-6 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{ background: 'radial-gradient(circle at 0% 50%, var(--accent), transparent 60%)' }}
        />
        <div className="relative flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl shrink-0"
            style={{ background: 'var(--accent)' }}
          >
            <i className="ri-shield-check-line"></i>
          </div>
          <div>
            <h2 className={`text-xl font-black ${textPrimary}`}>File a Warranty Claim</h2>
            <p className={`text-xs mt-0.5 ${textSecondary}`}>
              Complete the form below — we&apos;ll respond within 2 business days.
            </p>
          </div>
        </div>

        {/* Warranty period badges */}
        <div className={`flex flex-wrap gap-2 mt-5 pt-5 border-t ${divider}`}>
          {[
            { label: 'LiFePO4', period: '5-Year', color: '#10B981' },
            { label: 'AGM', period: '3-Year', color: '#0EA5E9' },
            { label: 'Gel', period: '2-Year', color: '#8B5CF6' },
          ].map((b) => (
            <div
              key={b.label}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ background: `${b.color}15`, color: b.color, border: `1px solid ${b.color}30` }}
            >
              <i className="ri-shield-check-line text-[11px]"></i>
              {b.label} — {b.period} Warranty
            </div>
          ))}
        </div>
      </div>

      {/* Form body */}
      <form
        data-readdy-form
        id="warranty-claim-form"
        onSubmit={handleSubmit}
        className={`px-8 pb-8 border-t ${divider}`}
      >
        <div className="pt-6 space-y-5">
          {/* Personal info */}
          <div>
            <p className={`text-xs font-black uppercase tracking-widest mb-4 ${textMuted}`}>
              <i className="ri-user-line mr-1.5" style={{ color: 'var(--accent)' }}></i>
              Your Information
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>First Name *</label>
                <input type="text" name="first_name" required placeholder="John" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Last Name *</label>
                <input type="text" name="last_name" required placeholder="Smith" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Email Address *</label>
                <input type="email" name="email" required placeholder="john@example.com" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Phone Number</label>
                <input type="tel" name="phone" placeholder="+1 (555) 000-0000" className={fieldCls} />
              </div>
            </div>
          </div>

          {/* Battery info */}
          <div className={`pt-5 border-t ${divider}`}>
            <p className={`text-xs font-black uppercase tracking-widest mb-4 ${textMuted}`}>
              <i className="ri-battery-line mr-1.5" style={{ color: 'var(--accent)' }}></i>
              Battery Information
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Battery Type *</label>
                <select
                  name="battery_type"
                  required
                  value={batteryType}
                  onChange={(e) => setBatteryType(e.target.value)}
                  className={`${fieldCls} cursor-pointer`}
                >
                  <option value="">Select battery type</option>
                  <option value="LiFePO4 Lithium">LiFePO4 Lithium (5-Year Warranty)</option>
                  <option value="AGM">AGM (3-Year Warranty)</option>
                  <option value="Gel">Gel (2-Year Warranty)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Battery Model / SKU *</label>
                <input type="text" name="battery_model" required placeholder="e.g. SR-LI-100" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Serial Number *</label>
                <input type="text" name="serial_number" required placeholder="Found on battery label" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Purchase Date *</label>
                <input type="date" name="purchase_date" required className={fieldCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Retailer / Dealer Name *</label>
                <input type="text" name="retailer_name" required placeholder="Where did you purchase the battery?" className={fieldCls} />
              </div>
            </div>
          </div>

          {/* Issue details */}
          <div className={`pt-5 border-t ${divider}`}>
            <p className={`text-xs font-black uppercase tracking-widest mb-4 ${textMuted}`}>
              <i className="ri-error-warning-line mr-1.5" style={{ color: 'var(--accent)' }}></i>
              Issue Details
            </p>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Type of Issue *</label>
                <select name="issue_type" required className={`${fieldCls} cursor-pointer`}>
                  <option value="">Select issue type</option>
                  <option value="Premature capacity loss">Premature capacity loss</option>
                  <option value="Battery not holding charge">Battery not holding charge</option>
                  <option value="Physical defect / damage">Physical defect / damage</option>
                  <option value="BMS failure">BMS failure (Lithium only)</option>
                  <option value="Swelling or deformation">Swelling or deformation</option>
                  <option value="Terminal / connection issue">Terminal / connection issue</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>
                  Describe the Issue *
                  <span className={`ml-2 normal-case font-normal ${textMuted}`}>({charCount}/500)</span>
                </label>
                <textarea
                  name="issue_description"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="Please describe the problem in detail — when it started, how the battery is being used, any error codes or symptoms..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className={`${fieldCls} resize-none`}
                />
                {charCount > 500 && (
                  <p className="text-red-400 text-xs mt-1">Description exceeds 500 character limit.</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Vessel / Application Type</label>
                <select name="vessel_type" className={`${fieldCls} cursor-pointer`}>
                  <option value="">Select vessel type (optional)</option>
                  <option value="Powerboat / Motorboat">Powerboat / Motorboat</option>
                  <option value="Sailboat">Sailboat</option>
                  <option value="Fishing boat">Fishing boat</option>
                  <option value="Pontoon">Pontoon</option>
                  <option value="Yacht">Yacht</option>
                  <option value="Commercial vessel">Commercial vessel</option>
                  <option value="RV / Land vehicle">RV / Land vehicle</option>
                  <option value="Solar / Off-grid">Solar / Off-grid</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Proof of purchase note */}
          <div
            className="flex items-start gap-3 p-4 rounded-xl"
            style={{ background: 'color-mix(in srgb, var(--accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' }}
          >
            <i className="ri-information-line mt-0.5 shrink-0" style={{ color: 'var(--accent)' }}></i>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--accent)' }}>
              <strong>Proof of purchase required.</strong> After submitting this form, our team will contact you to request a copy of your receipt or invoice. Claims without proof of purchase cannot be processed.
            </p>
          </div>

          {status === 'error' && (
            <p className="text-red-400 text-sm flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              Something went wrong. Please try again or contact us directly.
            </p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={status === 'loading' || charCount > 500}
            className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'var(--accent)' }}
          >
            {status === 'loading' ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Submitting Claim...
              </span>
            ) : (
              <span>
                <i className="ri-shield-check-line mr-2"></i>
                Submit Warranty Claim
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
